import { useState } from 'react'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  useCheckRendererCoverageMutation,
  useLazyGetAuthoringOpenApiyamlQuery,
  useLazyGetRendererContractQuery,
  type RendererCoverage,
  type RendererManifest,
} from '../../api/authoring'
import { problemMessage } from '../../api/baseApi'
import { download } from '../../ui/download'
import { shown } from '../../api/useIntent'

/**
 * What somebody building this tenant's website needs from the platform:
 * the contract their renderer has to meet, a check that a release of it
 * does, and the API's own description.
 *
 * All three were served and none was on a screen, so a developer learned
 * of them from the contract they could not find without already having
 * it. They sit with the webhooks because the reader is the same person —
 * whoever wires the site to the platform — and that person is not the
 * one editing pages.
 */
export function ForDevelopers({ tenant }: { tenant: string }) {
  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Typography variant="h2">For developers</Typography>
        <Typography color="text.secondary">
          For whoever builds the website that shows this content. Nothing here changes the site.
        </Typography>
      </Stack>
      <RendererContract tenant={tenant} />
      <RendererCheck tenant={tenant} />
      <ApiReference />
    </Stack>
  )
}

function RendererContract({ tenant }: { tenant: string }) {
  const [read, { isFetching, error }] = useLazyGetRendererContractQuery()

  const save = async () => {
    const contract = await read({ tenant }).unwrap()
    download(`${tenant}-renderer-contract.json`, JSON.stringify(contract, null, 2), 'application/json')
  }

  return (
    <Paper sx={{ p: 2.5 }}>
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="h3">What the website has to draw</Typography>
        <Typography variant="body2" color="text.secondary">
          Every kind of block this site can contain, the regions each one holds, and the page regions a theme has to
          draw. A theme written from this file draws everything an editor can add.
        </Typography>
        {Boolean(error) && <Alert severity="error">{problemMessage(error, 'The contract could not be read.')}</Alert>}
        <Button variant="outlined" disabled={isFetching} onClick={() => void save().catch(() => undefined)}>
          Download the contract
        </Button>
      </Stack>
    </Paper>
  )
}

function RendererCheck({ tenant }: { tenant: string }) {
  const [check, { isLoading, error, reset }] = useCheckRendererCoverageMutation()
  const [text, setText] = useState('')
  const [unreadable, setUnreadable] = useState<string>()
  const [report, setReport] = useState<RendererCoverage>()

  const run = async () => {
    reset()
    setReport(undefined)
    let manifest: RendererManifest
    try {
      manifest = JSON.parse(text) as RendererManifest
    } catch {
      setUnreadable('That is not valid JSON. Paste the manifest file exactly as the build wrote it.')
      return
    }
    setUnreadable(undefined)
    try {
      setReport(await check({ tenant, rendererManifest: manifest }).unwrap())
    } catch {
      // The mutation holds the refusal, and it is drawn below.
    }
  }

  return (
    <Paper sx={{ p: 2.5 }}>
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="h3">Check a website release</Typography>
        <Typography variant="body2" color="text.secondary">
          Paste the manifest a release of the website publishes, and this says whether that release can draw everything
          this site can contain. Run it before the release goes live.
        </Typography>
        <TextField
          label="The release's manifest"
          value={text}
          onChange={(event) => setText(event.target.value)}
          multiline
          minRows={4}
          maxRows={14}
          fullWidth
          placeholder='{ "presentation": "…", "release": "…", "terminals": [], "slots": [] }'
          slotProps={{ htmlInput: { style: { fontFamily: 'ui-monospace, monospace', fontSize: 13 } } }}
        />
        {unreadable && <Alert severity="error">{unreadable}</Alert>}
        {Boolean(error) && <Alert severity="error">{problemMessage(error, 'The check could not be run.')}</Alert>}
        <Button variant="outlined" disabled={isLoading || text.trim() === ''} onClick={() => run().catch(shown)}>
          Check it
        </Button>
        {report && <Report report={report} />}
      </Stack>
    </Paper>
  )
}

function Report({ report }: { report: RendererCoverage }) {
  const release = [report.presentation, report.release].filter(Boolean).join(' ')
  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      <Alert severity={report.covered ? 'success' : 'warning'}>
        <AlertTitle>
          {report.covered
            ? `${release || 'This release'} draws everything this site can contain`
            : `${release || 'This release'} cannot draw everything this site can contain`}
        </AlertTitle>
        {report.covered
          ? 'This was true when it was checked. A pattern added later is not covered by this answer.'
          : `${report.findings.length} thing${report.findings.length === 1 ? ' is' : 's are'} missing.`}
      </Alert>
      {report.findings.length > 0 && (
        <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 3 }}>
          {report.findings.map((finding, index) => (
            <Typography component="li" variant="body2" key={index}>
              {finding.detail}
            </Typography>
          ))}
        </Stack>
      )}
      {(report.undigested?.length ?? 0) > 0 && (
        <Typography variant="caption" color="text.secondary">
          Compared by name only, because the manifest gave no contract digest for them:{' '}
          {report.undigested?.join(', ')}.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary">
        Contract digest {report.digest}
      </Typography>
    </Stack>
  )
}

function ApiReference() {
  const [read, { isFetching, error }] = useLazyGetAuthoringOpenApiyamlQuery()

  const save = async () => {
    const contract = await read().unwrap()
    download('bluu-cms-authoring.openapi.yaml', String(contract), 'application/yaml')
  }

  return (
    <Paper sx={{ p: 2.5 }}>
      <Stack spacing={1.5} alignItems="flex-start">
        <Typography variant="h3">API reference</Typography>
        <Typography variant="body2" color="text.secondary">
          Everything this admin does, it does through one API. This is its description, as an OpenAPI document.
        </Typography>
        {Boolean(error) && <Alert severity="error">{problemMessage(error, 'The reference could not be read.')}</Alert>}
        <Button variant="outlined" disabled={isFetching} onClick={() => void save().catch(shown)}>
          Download the API reference
        </Button>
      </Stack>
    </Paper>
  )
}
